import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { DirectionalHint, Callout } from '@fluentui/react/lib/Callout';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { FocusTrapZone } from '@fluentui/react/lib/FocusTrapZone';
import { discardQuickComposeEvent } from 'owa-calendar-compose-form-actions';
import isQuickComposeInEditMode from 'owa-calendar-compose-form-store/lib/selectors/isQuickComposeInEditMode';
import { lazyIsQuickComposeOpen } from 'owa-calendar-compose-form-store';
import { getQuickComposeViewState, getComposeEvent } from 'owa-calendar-compose-store-manager';
import QuickCompose from './QuickCompose';
import { useRepositionableCallout } from 'owa-react-hooks/lib/useRepositionableCallout';
import { isFeatureEnabled } from 'owa-feature-flags';
import isQuickComposeViewTypeAssetNewEvent from 'owa-calendar-compose-form-store/lib/selectors/isQuickComposeViewTypeAssetNewEvent';

import styles from './QuickComposeRenderer.scss';
import classnamesBind from 'classnames/bind';
const classNames = classnamesBind.bind(styles);

/**
 * Props for quick compose renderer
 */
export interface QuickComposeRendererProps {
    formId?: string;
    shouldCoverTarget: (string) => boolean;
}

function useForceUpdate() {
    const [, forceUpdate] = React.useReducer<React.Reducer<number, void>>(x => x + 1, 0);
    return forceUpdate as () => void;
}

/**
 * Component for rendering quick compose
 */
const QuickComposeRenderer: React.FunctionComponent<QuickComposeRendererProps> = props => {
    const { shouldCoverTarget } = props;

    const formId = props.formId ?? null;

    // When the size of the compose form is changed and will go off the screen, quick compose
    // needs to be rerendered so the callout can reposition itself so it is on screen
    const onSizeUpdate = useForceUpdate();

    const {
        onMouseDown,
        onPositioned,
        calloutStyles,
        manuallyRepositioned,
    } = useRepositionableCallout();

    const onDismiss = React.useCallback(
        (ev?: any) => {
            // Dismiss peek unless the focus target is part of persona live card or the modal dialog or the location card
            var hasLPC =
                document.getElementById('LivePersonaCardRootElement')?.children.length > 0 ||
                document.getElementById('ImmersiveProfileOverlayWrapper')?.children.length > 0;
            var modal = document.getElementsByClassName('ms-Modal');
            if (
                !hasLPC &&
                (modal.length == 0 || (ev && modal.length > 0 && !modal[0].contains(ev.target)))
            ) {
                discardQuickComposeEvent(formId);
            }
        },
        [formId]
    );

    const onKeyDown = React.useCallback(
        (evt: React.KeyboardEvent<unknown>) => {
            if (evt.which == KeyCodes.escape) {
                evt.stopPropagation();
                evt.preventDefault();
                onDismiss(formId);
            }
        },
        [formId]
    );

    if (!lazyIsQuickComposeOpen.tryImportForRender()?.(formId)) {
        return null;
    }

    const surfaceAnchor = getQuickComposeViewState(formId).surfaceAnchor;
    const isInEditMode = isQuickComposeInEditMode(formId);

    const coverTarget = isInEditMode
        ? shouldCoverTarget(getComposeEvent(formId).ItemId.Id)
        : shouldCoverTarget(surfaceAnchor as string);
    const directionalHint = isQuickComposeViewTypeAssetNewEvent(formId)
        ? DirectionalHint.rightTopEdge
        : DirectionalHint.rightCenter;

    return (
        <Callout
            className={classNames(styles.callout, { isInEditMode: isInEditMode })}
            isBeakVisible={true}
            directionalHint={directionalHint}
            target={surfaceAnchor}
            onDismiss={onDismiss}
            preventDismissOnScroll={true}
            coverTarget={coverTarget}
            onPositioned={onPositioned}
            styles={calloutStyles}
            onMouseDown={isFeatureEnabled('cal-surface-dragPeek') ? onMouseDown : undefined}
            beakWidth={manuallyRepositioned ? 0 : undefined} // if repositioned manually, hide the beak
            shouldDismissOnWindowFocus={false}>
            <FocusTrapZone isClickableOutsideFocusTrap={true} forceFocusInsideTrap={false}>
                <QuickCompose formId={formId} onKeyDown={onKeyDown} onSizeUpdate={onSizeUpdate} />
            </FocusTrapZone>
        </Callout>
    );
};

export default observer(QuickComposeRenderer);
